/**
 * The editor's middle pane: the selected snapshot running in the real engine.
 *
 * The iframe is the playEngine's dashboard-mode bundle, served straight from the
 * agentContainer's build output — the same engine build the agent verifies
 * against, so what is on screen here is what ships.
 *
 * Unlike the version editor's preview there is no asset hydration step: a
 * snapshot already carries its assets inline as base64, so the file goes to the
 * engine exactly as it sits on disk.
 *
 * The iframe is remounted whenever the selected snapshot changes, mirroring what
 * the version editor does on a version switch — the engine builds its scene once
 * at boot and does not tear it down for a new snapshot.
 *
 * Drag handles on the iframe match the version editor's preview: right / bottom /
 * corner resize, with a live WxH overlay while dragging.
 *
 * The frame is always rendered at its full device resolution and then CSS-scaled
 * to fit the stage — the playable lays itself out for the pixel size it is given,
 * so shrinking the iframe itself would preview a different playable. Zoom is
 * therefore purely a transform on the wrapper; every size the toolbar and the
 * drag maths talk about stays in unscaled device pixels.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Typography } from "@material-ui/core";
import { SNAPSHOT_IFRAME_ID, getSnapshotPreviewUri } from "../../modules/snapshotEditorApi";
import ToolbarSnapshotPreview from "./toolbarSnapshotPreview";
import DialogExport from "./dialogExport";
import DialogShareLink from "./dialogShareLink";
import { CONTROL_BORDER, PANEL_BG } from "./panelPalette";
import {
  DEFAULT_DEVICE_ID,
  DEVICE_PRESETS,
  STAGE_INSET,
  ZOOM_FIT,
  ZOOM_STEPS,
  clampZoom,
  devicePresetById,
  orientedDeviceSize
} from "../../modules/previewFrame";

const SNAPSHOT_STAGE_ID = "snapshotPreviewStage";

const useStyles = makeStyles({
  root: { display: "flex", flex: 1, flexDirection: "column", minWidth: 0, minHeight: 0 },
  stage: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    // Room for the handles that sit 20px outside the iframe wrapper.
    paddingBottom: "30px",
    paddingRight: "30px",
    minWidth: 0,
    minHeight: 0,
    position: "relative",
    // Fit mode never overflows, but a zoom the user picked deliberately may —
    // scrolling it is honest, and it keeps the frame off the neighbouring panes.
    overflow: "auto",
    // A surface for the frame to sit on. Without it a playable whose own
    // background is dark bleeds into the panel and the device edge is guesswork.
    backgroundColor: PANEL_BG,
  },
  // Occupies the frame's *scaled* size, so the stage centres and scrolls against
  // what is actually on screen. The handles hang off this rather than off the
  // scaled frame, which keeps them the same size at every zoom.
  zoomBox: {
    position: "relative",
    flexShrink: 0,
    // Centres like the stage's own alignment would, but stays scrollable when a
    // hand-picked zoom overflows: `justify-content: center` puts the overflowing
    // half past the scroll origin, where it cannot be reached.
    margin: "auto",
    // Marks exactly where the device ends, so content running to (or past) the
    // edge is visible instead of blending into the stage. Deliberately a ring
    // rather than a border: box-shadow paints outside the box and leaves
    // offsetWidth/offsetHeight alone, which the resize maths reads.
    boxShadow: `0 0 0 1px ${CONTROL_BORDER}, 0 2px 12px rgba(0, 0, 0, 0.45)`,
  },
  // The device box at 1:1, scaled down into the zoom box above it.
  iframeWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    transformOrigin: "top left",
  },
  iframe: { borderStyle: "none", display: "block" },
  // The handles carry no size-dependent offsets of their own — every edge that
  // follows the frame (left/top/width/height) is written inline per render. See
  // `handleGeometry` below for why.
  resizeHandleRight: {
    position: "absolute",
    top: 0,
    width: 14,
    cursor: "ew-resize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    "& > div": {
      width: 4,
      height: "40%",
      minHeight: 24,
      background: "#595959",
      borderRadius: 3,
      transition: "background 0.15s, height 0.15s",
    },
    "&:hover > div": {
      background: "#4eabb1",
      height: "55%",
    },
  },
  resizeHandleBottom: {
    position: "absolute",
    left: 0,
    height: 14,
    cursor: "ns-resize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    "& > div": {
      height: 4,
      width: "40%",
      minWidth: 24,
      background: "#595959",
      borderRadius: 3,
      transition: "background 0.15s, width 0.15s",
    },
    "&:hover > div": {
      background: "#4eabb1",
      width: "55%",
    },
  },
  resizeHandleCorner: {
    position: "absolute",
    width: 20,
    height: 20,
    cursor: "nwse-resize",
    zIndex: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#595959",
    transition: "color 0.15s",
    "&:hover": {
      color: "#4eabb1",
    },
  },
  dragSizeOverlay: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    background: "rgba(0,0,0,0.75)",
    color: "#fff",
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 13,
    fontFamily: "monospace",
    pointerEvents: "none",
    zIndex: 20,
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  placeholder: { opacity: 0.7, fontSize: "14px" },
});

export default function ContainerSnapshotPreview({ editor, createSnackbar }) {
  const classes = useStyles();

  const { isPortrait, setIsPortrait } = editor;
  const [reloadCount, setReloadCount] = useState(0);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  // Silences the preview and NOTHING else. It is a property of watching, not of
  // the playable: the snapshot's own masterVolume is untouched, so what ships is
  // whatever the Sounds tab says it is. Off by default, and reset per snapshot
  // below — the first time you see a playable should be the way its audience
  // will see it.
  const [isMuted, setIsMuted] = useState(false);
  const [iframeSize, setIframeSize] = useState(null); // { w, h } | null — manual drag
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const dragCleanupRef = useRef(null);
  // ZOOM_FIT (the default) or a number. Fit is what keeps a landscape frame —
  // wider than this pane at every preset — inside the stage instead of running
  // out under the snapshot list and the settings panel.
  const [zoom, setZoom] = useState(ZOOM_FIT);
  const [stageSize, setStageSize] = useState(null); // { width, height } of the stage box
  const stageRef = useRef(null);

  // One engine per boot, and a boot is a file plus a reload. Tracking readiness
  // by that key rather than by a boolean means switching files or reloading
  // makes the engine un-ready by construction — there is no flag to forget to
  // reset, and nothing posts a snapshot into an iframe that is still loading.
  // Deliberately NOT keyed on contentHash: every save mints a new hash, so the
  // engine would reboot 600ms after each edit; the counter covers a forced
  // reload instead.
  //
  // Keyed on the SELECTED file, not the loaded one. The engine bundle is ~700kb
  // and has nothing to do with which snapshot it will be given, but keying on
  // `loadedFileName` made it wait for one anyway: the fetch clears `loaded`
  // before it starts, so the iframe unmounted on click and only came back once
  // several megabytes of snapshot had arrived and parsed. Downloading the bundle
  // and booting the engine now overlap the snapshot fetch; only the handover
  // waits, which it must — see deliverSnapshot.
  const iframeKey = editor.selectedFileName ? `${editor.selectedFileName}-${reloadCount}` : null;
  const [readyKey, setReadyKey] = useState(null);
  const isEngineLoaded = Boolean(iframeKey) && readyKey === iframeKey;
  // `playengine:ready` only requests the snapshot. Runtime actions are accepted
  // after restore, when `playengine:playable-ready` arrives from this iframe.
  const playableReadyIframeRef = useRef(null);
  const [playableReadyCount, setPlayableReadyCount] = useState(0);

  // Read inside the message handler, which is registered once per mount and must
  // not close over a stale snapshot.
  const snapshotRef = useRef(editor.snapshot);
  snapshotRef.current = editor.snapshot;
  const iframeKeyRef = useRef(iframeKey);
  iframeKeyRef.current = iframeKey;
  // The boot this browser has already handed a snapshot to. The engine takes
  // exactly one per boot and ignores the rest, so anything past the first is
  // waste — and the waste is a structured clone of every inline base64 byte.
  const deliveredKeyRef = useRef(null);

  const defaultSize = orientedDeviceSize(devicePresetById(deviceId), isPortrait);
  const displayWidth = iframeSize ? iframeSize.w : defaultSize.width;
  const displayHeight = iframeSize ? iframeSize.h : defaultSize.height;

  // The largest scale that still leaves the whole frame (and its handles) inside
  // the stage. Never above 1:1 — fit is there to rescue an oversized frame, and
  // silently blowing a small one up to fill the pane is not what it promises.
  const fitScale = useMemo(() => {
    if (!stageSize || !displayWidth || !displayHeight) return 1;
    const availableWidth = Math.max(1, stageSize.width - STAGE_INSET);
    const availableHeight = Math.max(1, stageSize.height - STAGE_INSET);
    return clampZoom(
      Math.min(1, availableWidth / displayWidth, availableHeight / displayHeight)
    );
  }, [displayHeight, displayWidth, stageSize]);

  const scale = zoom === ZOOM_FIT ? fitScale : clampZoom(zoom);

  // A tab/file switch can unmount the preview while a resize gesture still
  // owns document.body.userSelect. Always release that global lock; otherwise
  // every text surface in the dashboard remains unselectable.
  useEffect(() => () => {
    dragCleanupRef.current?.(false);
  }, []);

  // The zoom box's on-screen size, and with it every handle offset.
  //
  // Floored, not rounded: a box a rounded-up pixel wider than the fit maths
  // allowed for would raise a scrollbar, which takes that pixel back off the
  // stage and starts the fit oscillating.
  const boxWidth = Math.floor(displayWidth * scale);
  const boxHeight = Math.floor(displayHeight * scale);

  // Handle placement, in pixels, written onto the handles themselves.
  //
  // The obvious spelling is to hang them off the zoom box — `right: -20`,
  // `top: 0; bottom: 0` — and let the box's own size stretch them. Safari does
  // not reliably re-lay-out those children when the box resizes mid-drag: the
  // box contains a transformed (composited) iframe, and only the handle under
  // the captured pointer gets refreshed. Drag the bottom edge and the right and
  // corner handles stay painted against the frame's PREVIOUS height, drifting
  // further off with every pixel.
  //
  // So nothing here resolves against the parent. Each handle gets explicit
  // left/top/width/height on its own style attribute, which changes every
  // render and therefore cannot go stale. The numbers reproduce what the old
  // offsets computed: the trailing handles sit in the 20px gutter outside the
  // frame, the corner one flush with its bottom-right corner.
  const handleGeometry = {
    right: { left: boxWidth + 6, height: boxHeight },
    bottom: { top: boxHeight + 6, width: boxWidth },
    corner: { left: boxWidth, top: boxHeight },
    overlay: { left: Math.round(boxWidth / 2), top: Math.round(boxHeight / 2) },
  };

  // Fit has to react to the pane getting narrower (the settings panel opening,
  // the window resizing), not just to a new device — so the stage is measured
  // rather than assumed.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      setStageSize((current) =>
        current && current.width === stage.clientWidth && current.height === stage.clientHeight
          ? current
          : { width: stage.clientWidth, height: stage.clientHeight }
      );
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [editor.selectedFileName]);

  /**
   * Hand the snapshot to the engine — at most once per boot.
   *
   * The two halves of a snapshot switch now race each other: the engine boots
   * while the file downloads, and either can win. So this is written to be
   * called from every arrival point and to do nothing until BOTH have landed:
   * the engine has announced itself for the boot currently on screen, and the
   * file it is meant to show is in hand.
   *
   * `deliveredKeyRef` is what makes it once. The engine accepts one snapshot per
   * boot and ignores the rest, and a repeat is not free — postMessage structured-
   * clones every inline base64 byte, so a stray second call is a multi-megabyte
   * copy the engine throws away.
   */
  const deliverSnapshot = useCallback(() => {
    const snapshot = snapshotRef.current;
    const key = iframeKeyRef.current;
    if (!snapshot || !key) return;
    if (deliveredKeyRef.current === key) return;

    const iframe = document.getElementById(SNAPSHOT_IFRAME_ID);
    if (!iframe || !iframe.contentWindow) return;

    deliveredKeyRef.current = key;
    iframe.contentWindow.postMessage({ type: "playengine:load-snapshot", snapshot }, "*");
  }, []);

  // A new boot has not been fed yet. Declared before the delivery effects so it
  // runs first in the commit that changes the key.
  //
  // Clearing this is not optional: keys repeat. Open A, switch to B, come back
  // to A and the key is `A.json-0` again — but the iframe in between was a real
  // unmount, so this is a brand new engine with nothing in it. Without the reset
  // the guard would recognise the old key and leave the preview blank.
  useEffect(() => {
    deliveredKeyRef.current = null;
  }, [iframeKey]);

  // The engine announces itself when it is ready for a snapshot. This is the
  // primary delivery path; the iframe's onLoad below is only a safety net for
  // the case where the announcement lands before this listener is attached.
  useEffect(() => {
    function handleMessage(event) {
      if (event.data?.type !== "playengine:ready" && event.data?.type !== "playengine:playable-ready") return;
      const iframe = document.getElementById(SNAPSHOT_IFRAME_ID);
      if (!iframe || event.source !== iframe.contentWindow) return;
      if (event.data.type === "playengine:playable-ready") {
        playableReadyIframeRef.current = iframe;
        setPlayableReadyCount((count) => count + 1);
        return;
      }
      setReadyKey(iframeKeyRef.current);
      deliverSnapshot();
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [deliverSnapshot]);

  // The other side of the race: the engine was up first and the file has just
  // arrived. Also covers a snapshot replaced in place — a 409 reload puts the
  // on-disk truth into an engine that is already running.
  //
  // `editor.snapshot` takes a new identity on every keystroke, so this fires
  // constantly while editing; deliverSnapshot's per-boot guard is what keeps
  // that from re-posting the whole file each time.
  useEffect(() => {
    if (editor.snapshot && isEngineLoaded) deliverSnapshot();
  }, [editor.snapshot, isEngineLoaded, deliverSnapshot]);

  // A new snapshot starts audible. Muting answers "I don't want to hear this
  // one right now", which does not carry over to the next playable.
  useEffect(() => {
    setIsMuted(false);
  }, [editor.selectedFileName]);

  /**
   * Tell the engine on screen whether it may make a sound.
   *
   * `muteGame` / `unmuteGame` are engine actions (MasterAudioController): they
   * move the master gain and leave `generalSettings.masterVolume` alone, which
   * is the whole point — the General Settings panel reads its values back out
   * of the live engine, so muting through the volume would show up there and
   * eventually be persisted into the snapshot as a real setting.
   *
   * Re-sent after every completed restore, not just on the toggle. The earlier
   * `playengine:ready` signal fires before the action listener exists, so a
   * command sent at that point is lost on restart and snapshot reload.
   */
  useEffect(() => {
    const iframe = document.getElementById(SNAPSHOT_IFRAME_ID);
    if (!iframe || !iframe.contentWindow || playableReadyIframeRef.current !== iframe) return;

    iframe.contentWindow.postMessage(
      {
        type: "playengine:dispatch-action",
        action: { target: "engine", name: isMuted ? "muteGame" : "unmuteGame" },
      },
      "*"
    );
  }, [iframeKey, playableReadyCount, isMuted]);

  /**
   * Toolbar reload: reboot the engine, nothing else.
   *
   * This is a "play it again" button. The playable runs once — an intro, a fail
   * state, a transition you blinked through — and the only way back to the top
   * is a fresh boot, so it has to be instant and it has to be the same playable
   * you were just looking at.
   *
   * Deliberately no trip to the server. The engine is fed from the snapshot this
   * browser holds, which is the editor's live copy: it already carries the edits
   * you just made, debounced saves included, so a re-read could only replace it
   * with something older. Picking up a change written to disk out of band is a
   * different job with a different gesture — reselect the snapshot (or another
   * and back) and the editor loads it fresh.
   *
   * Bumping the counter is the whole mechanism: it moves `iframeKey`, React
   * unmounts the iframe and mounts a new one, and the delivery effects hand the
   * snapshot to the new engine exactly as they do on a first boot.
   *
   * The agent pipeline does not come through here — it re-selects or reloads the
   * files it produced through the editor hook, which is why that path still
   * reaches disk.
   */
  const reload = useCallback(() => {
    setReloadCount((count) => count + 1);
  }, []);

  /**
   * Reload requests from a shared panel.
   *
   * The playEngine settings panel is used by BOTH editors, and applying a new
   * canvas base dimension has to restart the engine — the design space moved,
   * so the scene must be rebuilt in it. Rather than thread a reload callback
   * through two different parents, the panel raises the same window event the
   * agent path already uses and each preview answers it with its own reload.
   */
  useEffect(() => {
    const handler = () => reload();
    window.addEventListener("playengine:agent-reload", handler);
    return () => window.removeEventListener("playengine:agent-reload", handler);
  }, [reload]);

  function startDrag(event, edge) {
    event.preventDefault();
    event.stopPropagation();

    // Defensive against a second pointerdown before the previous gesture got a
    // terminal event.
    dragCleanupRef.current?.(false);

    const iframe = document.getElementById(SNAPSHOT_IFRAME_ID);
    const stage = document.getElementById(SNAPSHOT_STAGE_ID);
    const currentW = iframeSize
      ? iframeSize.w
      : iframe
        ? Math.round(iframe.offsetWidth || parseInt(iframe.width, 10) || defaultSize.width)
        : defaultSize.width;
    const currentH = iframeSize
      ? iframeSize.h
      : iframe
        ? Math.round(iframe.offsetHeight || parseInt(iframe.height, 10) || defaultSize.height)
        : defaultSize.height;

    // Wrapper is centered; handles sit 20px outside — keep the drag inside the
    // stage. The limit is on the device size, so the on-screen room the stage
    // has converts back through the zoom before it becomes a cap.
    const maxW = stage ? Math.max(120, (stage.clientWidth - STAGE_INSET) / scale) : 2048;
    const maxH = stage ? Math.max(120, (stage.clientHeight - STAGE_INSET) / scale) : 2048;

    dragStartRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startW: currentW,
      startH: currentH,
      edge,
      lastW: currentW,
      lastH: currentH,
      maxW,
      maxH,
      scale,
    };
    // Capture the pointer for the duration of the drag.
    //
    // What is being resized is an IFRAME, and a pointer that crosses into one
    // stops delivering events to this document — they go to the playable's own
    // document instead. Pull the handle inwards quickly and the cursor overtakes
    // the shrinking frame; the release then lands inside the iframe, this
    // document never sees the pointerup, and the frame keeps following the mouse
    // with no button held. Capture retargets every event to the handle until the
    // release, wherever the cursor happens to be — including outside the window,
    // the other way a plain mouseup goes missing.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_err) {
      // No capture available: the listeners below still run, they are just
      // interruptible by the iframe again.
    }

    // Dragging under Fit would be a tug of war — every pixel the frame gains,
    // fit takes straight back off the scale and nothing moves. Freezing the zoom
    // at what is already on screen makes the handle do what it looks like it does.
    if (zoom === ZOOM_FIT) setZoom(scale);
    setIsDragging(true);
    setIframeSize({ w: currentW, h: currentH });
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor =
      edge === "right" ? "ew-resize" : edge === "bottom" ? "ns-resize" : "nwse-resize";
    document.body.style.userSelect = "none";
    const captureTarget = event.currentTarget;
    const pointerId = event.pointerId;
    let finished = false;

    // Captured events are retargeted to the handle but still bubble to the
    // document, so these keep working either way.
    function onPointerMove(moveEvent) {
      const {
        startX,
        startY,
        startW,
        startH,
        edge: dragEdge,
        maxW: mxW,
        maxH: mxH,
        scale: dragScale,
      } = dragStartRef.current;
      // The pointer moves in screen pixels; the frame is measured in device
      // pixels, and at 0.5x one of those is half a screen pixel.
      const dx = (moveEvent.clientX - startX) / dragScale;
      const dy = (moveEvent.clientY - startY) / dragScale;
      let newW = startW;
      let newH = startH;
      // Centered wrapper: each edge only contributes half the size change.
      if (dragEdge === "right" || dragEdge === "corner") {
        newW = Math.min(mxW, Math.max(120, Math.round(startW + 2 * dx)));
      }
      if (dragEdge === "bottom" || dragEdge === "corner") {
        newH = Math.min(mxH, Math.max(120, Math.round(startH + 2 * dy)));
      }
      dragStartRef.current.lastW = newW;
      dragStartRef.current.lastH = newH;

      const liveIframe = document.getElementById(SNAPSHOT_IFRAME_ID);
      if (liveIframe) {
        liveIframe.width = newW;
        liveIframe.height = newH;
      }
      setIframeSize({ w: newW, h: newH });
    }

    // pointercancel too: the browser can take the pointer away mid-drag (a
    // touch turning into a scroll, the window losing it), and that has to end
    // the drag for the same reason a missing pointerup must not.
    function finishDrag(commit = true) {
      if (finished) return;
      finished = true;
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
      captureTarget.removeEventListener("lostpointercapture", onLostPointerCapture);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      dragCleanupRef.current = null;

      try {
        if (captureTarget.hasPointerCapture(pointerId)) captureTarget.releasePointerCapture(pointerId);
      } catch (_err) {
        // Capture was already released by the browser.
      }

      if (!commit) return;
      setIsDragging(false);

      const finalW = dragStartRef.current.lastW;
      const finalH = dragStartRef.current.lastH;
      setIframeSize({ w: finalW, h: finalH });
      // Keep the orientation icon honest without wiping the custom size.
      setIsPortrait(finalH >= finalW);
    }

    function onPointerUp() {
      finishDrag(true);
    }

    function onWindowBlur() {
      finishDrag(true);
    }

    function onVisibilityChange() {
      if (document.hidden) finishDrag(true);
    }

    function onLostPointerCapture() {
      finishDrag(true);
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onWindowBlur);
    captureTarget.addEventListener("lostpointercapture", onLostPointerCapture);
    dragCleanupRef.current = finishDrag;
  }

  if (!editor.selectedFileName) {
    return (
      <div className={classes.root}>
        <div className={classes.stage}>
          <Typography className={classes.placeholder}>
            Select a snapshot to preview it.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ToolbarSnapshotPreview
        editor={editor}
        isPortrait={isPortrait}
        devices={DEVICE_PRESETS}
        deviceId={deviceId}
        // A dragged frame is nobody's preset, so the toolbar says so and shows
        // the live figures rather than the preset it no longer matches.
        isCustomSize={Boolean(iframeSize)}
        previewWidth={displayWidth}
        previewHeight={displayHeight}
        zoom={zoom}
        zoomSteps={ZOOM_STEPS}
        // What is on screen right now, which under Fit is the measured scale
        // rather than anything that was picked.
        effectiveZoom={scale}
        onSelectZoom={setZoom}
        onSelectDevice={(nextId) => {
          // Drop the dragged size, the same way switching orientation does —
          // otherwise picking a device would appear to do nothing. Zoom goes
          // back to Fit with it: a new frame is a new size, and the one it was
          // pinned at is very likely the wrong one for it.
          setIframeSize(null);
          setZoom(ZOOM_FIT);
          setDeviceId(nextId);
        }}
        onCustomSize={({ width, height }) => {
          // A typed size is a dragged size by another route: nobody's preset, so
          // it lands in the same place and reads back as "Custom size".
          setIframeSize({ w: width, h: height });
          setZoom(ZOOM_FIT);
          setIsPortrait(height >= width);
        }}
        onToggleOrientation={() => {
          setIframeSize(null);
          setZoom(ZOOM_FIT);
          setIsPortrait((value) => !value);
        }}
        isMuted={isMuted}
        onToggleMute={() => setIsMuted((value) => !value)}
        onReload={reload}
        onExport={() => setIsExportOpen(true)}
        // Marks the button until the dialog is opened and the result taken:
        // the snackbar is gone long before anyone looks up.
        exportJob={editor.jobs.get("exportSnapshots")}
        onShareLink={() => setIsShareOpen(true)}
      />
      <DialogExport
        editor={editor}
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
      />
      <DialogShareLink
        editor={editor}
        open={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        createSnackbar={createSnackbar}
      />
      <div id={SNAPSHOT_STAGE_ID} className={classes.stage} ref={stageRef}>
        {/* Booted as soon as a file is SELECTED, alongside the fetch rather than
            after it. What must not race is the handover — the engine accepts
            exactly one snapshot per boot, so handing it the previous file's
            config would silently drop the one being fetched. deliverSnapshot
            enforces that instead: it posts only for the boot currently on
            screen, only once the matching file is in hand, and only once.

            The settings panels are gated separately, on the loaded file
            (containerSnapshotConfig.js) — they read the data, not the iframe, so
            an engine that boots earlier than they do costs them nothing. */}
        {iframeKey && (
          <div className={classes.zoomBox} style={{ width: boxWidth, height: boxHeight }}>
            <div
              className={classes.iframeWrapper}
              style={{
                width: displayWidth,
                height: displayHeight,
                transform: `scale(${scale})`,
              }}
            >
              <iframe
                allow="autoplay"
                id={SNAPSHOT_IFRAME_ID}
                key={iframeKey}
                title="Snapshot preview"
                className={classes.iframe}
                src={getSnapshotPreviewUri()}
                width={displayWidth}
                height={displayHeight}
                onLoad={() => {
                  // Safety net: if playengine:ready fired before the listener above
                  // was attached, waitForSnapshot would otherwise time out. Yield
                  // first so a queued ready event still wins.
                  setReadyKey(iframeKey);
                  window.setTimeout(deliverSnapshot, 0);
                }}
              />
            </div>
            <div
              className={classes.resizeHandleRight}
              style={handleGeometry.right}
              onPointerDown={(e) => startDrag(e, "right")}
            >
              <div />
            </div>
            <div
              className={classes.resizeHandleBottom}
              style={handleGeometry.bottom}
              onPointerDown={(e) => startDrag(e, "bottom")}
            >
              <div />
            </div>
            <div
              className={classes.resizeHandleCorner}
              style={handleGeometry.corner}
              onPointerDown={(e) => startDrag(e, "corner")}
            >
              <svg viewBox="0 0 10 10" width="10" height="10" style={{ display: "block", pointerEvents: "none" }}>
                <line x1="1" y1="9" x2="9" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="5" y1="9" x2="9" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            {isDragging && iframeSize && (
              <div className={classes.dragSizeOverlay} style={handleGeometry.overlay}>
                {iframeSize.w} × {iframeSize.h}
              </div>
            )}
          </div>
        )}
        {(editor.isSnapshotLoading || !editor.loadedFileName || !isEngineLoaded) && (
          <div className={classes.overlay}>
            <CircularProgress size={28} />
          </div>
        )}
      </div>
    </div>
  );
}
